<template>
  <div class="multi-picker-row">
    <SlottedRow
      :allow-delete="editing && displayField.allowDelete"
      :handle-delete="handleDelete"
      :uuid="displayField.uuid"
    >
      <template #label>
        <div class="display-field-text multi-picker-row-label">
          {{ displayField.label }}
        </div>
      </template>
      <template
        v-if="displayField.citation"
        #icon
      >
        <CitationIcon
          :display-field="displayField"
        />
      </template>
      <template #value>
        <div
          v-if="!editing || !displayField.allowEdit"
          class="display-field-text multi-picker-row-value"
        >
          {{ displayValue }}
        </div>
        <v-select
          v-else
          v-model="picks"
          :items="displayField.pickerOptions"
          bg-color="#EBEBEB"
          multiple
          :menu-props="{ contentClass: 'multi-picker-menu' }"
          :data-testid="`multi-picker-row-${displayField.label}`"
          class="display-field-text multi-picker-row-select"
          @update:model-value="checkPicks"
          @update:menu="handleSelectFocus"
        >
          <template #selection="{ index }">
            <span
              v-if="index === 0"
              class="display-field-text multi-picker-row-selection"
            >{{ displayValue }}</span>
          </template>
          <template #item="{ props: itemProps, item }">
            <v-list-item
              v-bind="itemProps"
              :title="item.title"
            >
              <template #prepend="{ isActive }">
                <v-icon
                  :icon="isActive ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"
                  :color="isActive ? 'orange' : undefined"
                  size="small"
                />
              </template>
            </v-list-item>
          </template>
        </v-select>
      </template>
    </SlottedRow>
  </div>
</template>

<script>
import SlottedRow from './SlottedRow.vue';
import {DisplayField} from '@/lib/classes/InfoWindowContent';
import CitationIcon from '@/components/InfoWindow/DisplayFields/CitationIcon.vue';

import {MULTI_PICKER_DELIMITER, parseMultiPickerValue, joinMultiPickerValue} from '@/lib/StandardFields';

export default {
  name: 'MultiPickerRow',
  components: {
    SlottedRow,
    CitationIcon,
  },
  props: {
    displayField: {
      type: DisplayField,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateDisplayField', 'deleteDisplayField'],
  data() {
    return {
      originalPicks: parseMultiPickerValue(this.displayField.value),
      picks: parseMultiPickerValue(this.displayField.value),
      dropdownExpanded: false,
    };
  },
  computed: {
    displayValue() {
      if (!this.picks || !this.picks.length) return '';
      return this.picks.join(`${MULTI_PICKER_DELIMITER} `);
    },
  },
  methods: {
    handleSelectFocus() {
      this.dropdownExpanded = !this.dropdownExpanded;

      if (!this.dropdownExpanded) {
        document.activeElement.blur();
      }
    },
    checkPicks() {
      const currentValue = joinMultiPickerValue(this.picks);
      const originalValue = joinMultiPickerValue(this.originalPicks);

      if (currentValue === originalValue) return;

      this.originalPicks = [...this.picks];

      this.$emit('updateDisplayField', new DisplayField({
        ...this.displayField,
        value: currentValue || null,
        pickerOptions: Array.from(this.displayField.pickerOptions),
      }));
    },
    handleDelete() {
      this.$emit('deleteDisplayField', this.displayField);
    },
  },
};
</script>

<style lang="scss" scoped>
.multi-picker-row {
  &-label {
    padding: 0 5px;
  }

  &-value {
    padding: 0 3px;
  }

  &-selection {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &-select {
    :deep(.v-field) {
      font-size: 13px;
      min-height: 23px;
      border-radius: 2px;
    }

    :deep(.v-input__details) {
      display: none
    }

    :deep(.v-field__input) {
      height: 23px;
      min-height: 23px;
      padding-inline-start: 0;
      padding-inline-end: 0;
      padding-top: 0;
      padding-bottom: 0;
      font-size: 13px;
      & input {
        position: absolute;
        right: 5px;
        top: 5px;
        font-size: 13px;
      }
    }

    :deep(.v-field__append-inner) {
      min-height: 23px;

      i {
        position: absolute;
        right: 5px;
        top: 3px;
        transition: none !important;
      }
    }

    :deep(.v-messages) {
      display: none;
    }

    :deep(.v-select__selection) {
      @extend .display-field-text;

      position: absolute;
      left: 0px;
      top: 0px;
      padding-left: 6px;
      padding-top: 3px;
      height: 23px;
      width: calc(100% - 24px);
      overflow: hidden;
    }

    :deep(.v-field__outline) {
      display: none;
    }

  }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
// These styles need to be global because they are targeting the menu that is teleported outside of this component's
// scope
.multi-picker-menu {
  .v-list-item {
    min-height: 32px;
    padding-inline: 8px !important;
  }

  .v-list-item__prepend {
    margin-inline-end: 0 !important;

    .v-list-item__spacer {
      width: 4px;
    }
  }
}
</style>
